// Centralized API configuration - using live API
const API_BASE_URL = 'https://nextstep.mayurr.in/api';

const request = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  const config = { ...options, headers };
  if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
  }
  
  try {
    const res = await fetch(url, config);
    const data = await res.json().catch(() => ({}));
    
    if (!res.ok) {
      const errorMessage = data.message || `HTTP ${res.status}: ${res.statusText}`;
      throw new Error(errorMessage);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

const getAuthHeaders = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = user?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth
export const loginUser = (userId, password) =>
  request('/auth/login', { method: 'POST', body: { userId, password } });

export const changePassword = (currentPassword, newPassword) =>
  request('/auth/change-password', {
    method: 'PUT',
    body: { currentPassword, newPassword },
    headers: getAuthHeaders(),
  });

export const requestAdminOtp = (userId) =>
  request('/auth/admin/send-otp', { method: 'POST', body: { userId } });

export const verifyAdminOtp = (userId, otp) =>
  request('/auth/admin/verify-otp', { method: 'POST', body: { userId, otp } });

// Users
export const fetchUsers = async (role) => {
  const url = role ? `/users?role=${role}` : '/users';
  const res = await request(url, { headers: getAuthHeaders() });
  return res.data || [];
};

export const createUser = (user) =>
  request('/users', { method: 'POST', body: user, headers: getAuthHeaders() });

export const fetchUserById = async (id) => {
  const res = await request(`/users/${id}`, { headers: getAuthHeaders() });
  return res.data;
};

export const fetchCurrentUser = async () => {
  const res = await request('/users/me', { headers: getAuthHeaders() });
  return res.data;
};

export const updateUser = (id, updates) =>
  request(`/users/${id}`, { method: 'PUT', body: updates, headers: getAuthHeaders() });

export const deleteUser = (id) =>
  request(`/users/${id}`, { method: 'DELETE', headers: getAuthHeaders() });

export const fetchAreaHeads = async () => {
  const res = await request('/users/area-heads/list', { headers: getAuthHeaders() });
  return res.data || [];
};

export const updateMyLocation = (latitude, longitude) =>
  request('/users/me/location', {
    method: 'PUT',
    body: { latitude, longitude },
    headers: getAuthHeaders(),
  });

// Tickets
export const fetchTickets = async (params = {}) => {
  const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
  const res = await request(`/tickets${qs}`, { headers: getAuthHeaders() });
  return Array.isArray(res.data) ? res.data : res.data?.data || [];
};

export const fetchTicketById = async (id) => {
  const res = await request(`/tickets/${id}`, { headers: getAuthHeaders() });
  return res.data;
};

export const createTicket = (ticket) =>
  request('/tickets', { method: 'POST', body: ticket, headers: getAuthHeaders() });

export const createTicketWithImage = async (formData) => {
  const host = getHost();
  const url = host ? `${host}/api/tickets/with-image` : '/api/tickets/with-image';
  const headers = getAuthHeaders();
  console.log('[API] Submitting to:', url);
  console.log('[API] Headers:', headers);
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...headers, Accept: 'application/json' },
    body: formData,
  });
  console.log('[API] Response status:', res.status);
  const data = await res.json().catch((e) => {
    console.error('[API] JSON parse error:', e);
    return {};
  });
  console.log('[API] Response data:', data);
  if (!res.ok) throw new Error(data.message || data.error || `Server error: ${res.status}`);
  return data;
};

const getHost = () => {
  const base = process.env.REACT_APP_API_URL || 'https://nextstep.mayurr.in/api';
  return base.replace('/api', '') || 'https://nextstep.mayurr.in';
};

export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  const host = 'https://nextstep.mayurr.in';
  return `${host}/uploads/${imagePath}`;
};

export const updateTicket = (id, updates) =>
  request(`/tickets/${id}`, { method: 'PUT', body: updates, headers: getAuthHeaders() });

export const submitTicketFeedback = (id, rating, comment) =>
  request(`/tickets/${id}/feedback`, {
    method: 'PUT',
    body: { rating, feedback_comment: comment || undefined },
    headers: getAuthHeaders(),
  });

export const assignTicket = (id, data) =>
  request(`/tickets/${id}/assign`, { method: 'PUT', body: data, headers: getAuthHeaders() });

export const verifyTicketCompletion = (id, otp) =>
  request(`/tickets/${id}/verify-completion`, { method: 'POST', body: { otp }, headers: getAuthHeaders() });

// Visits (AMC Preventive Maintenance)
export const fetchVisits = async (params = {}) => {
  const qs = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
  console.log('[API] Fetching visits with qs:', qs);
  const res = await request(`/visits${qs}`, { headers: getAuthHeaders() });
  console.log('[API] Visits raw response:', res);
  console.log('[API] res.data:', res.data);
  console.log('[API] res.data?.data:', res.data?.data);
  return res.data?.data || res.data || [];
};

export const fetchMyVisits = async (upcoming = false, days) => {
  const params = { upcoming };
  if (days) params.days = days;
  const qs = '?' + new URLSearchParams(params).toString();
  console.log('[API] Fetching my-visits with qs:', qs);
  const res = await request(`/visits/my-visits${qs}`, { headers: getAuthHeaders() });
  console.log('[API] My-visits raw response:', res);
  console.log('[API] res.data:', res.data);
  console.log('[API] res.data?.data:', res.data?.data);
  return res.data?.data || res.data || [];
};

export const fetchOverdueVisits = async () => {
  console.log('[API] Fetching overdue visits');
  const res = await request('/visits/overdue', { headers: getAuthHeaders() });
  console.log('[API] Overdue visits raw response:', res);
  return res.data?.data || res.data || [];
};

export const fetchVisitById = async (id) => {
  const res = await request(`/visits/${id}`, { headers: getAuthHeaders() });
  return res.data;
};

export const createVisit = (visit) =>
  request('/visits', { method: 'POST', body: visit, headers: getAuthHeaders() });

export const updateVisit = (id, visit) =>
  request(`/visits/${id}`, { method: 'PUT', body: visit, headers: getAuthHeaders() });

export const completeVisit = (id, completionNotes) =>
  request(`/visits/${id}/complete`, { method: 'PUT', body: { completionNotes }, headers: getAuthHeaders() });

export const deleteVisit = (id) =>
  request(`/visits/${id}`, { method: 'DELETE', headers: getAuthHeaders() });

export const fetchUserVisitStats = async (userId) => {
  const res = await request(`/visits/user/${userId}/stats`, { headers: getAuthHeaders() });
  return res.data;
};

export const checkVisitNeeded = async (userId) => {
  const res = await request(`/visits/user/${userId}/check`, { headers: getAuthHeaders() });
  return res.data;
};
